import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    CheckCircle2, XCircle, ChevronRight
} from 'lucide-react'
import { mockPendingReports, type StudentDailyReport } from '@/data/mentor-mock-data'
import { toast } from 'sonner'

interface ApprovalModalData {
    report: StudentDailyReport
    isOpen: boolean
}

export function ApprovalQueueView() {
    const [selectedReports, setSelectedReports] = useState<string[]>([])
    const [approvalModal, setApprovalModal] = useState<ApprovalModalData>({
        report: null as any,
        isOpen: false
    })
    const [approvedHours, setApprovedHours] = useState<Record<string, number>>({})
    const [mentorFeedback, setMentorFeedback] = useState('')

    const pendingReports = mockPendingReports.filter(r => r.approvalStatus === 'pending')

    const handleSelectReport = (reportId: string) => {
        if (selectedReports.includes(reportId)) {
            setSelectedReports(selectedReports.filter(id => id !== reportId))
        } else {
            setSelectedReports([...selectedReports, reportId])
        }
    }

    const handleSelectAll = () => {
        if (selectedReports.length === pendingReports.length) {
            setSelectedReports([])
        } else {
            setSelectedReports(pendingReports.map(r => r.id))
        }
    }

    const handleQuickApprove = (reportId: string, hours: number) => {
        toast.success(`Approved ${hours}h for student`)
        // Remove from selected
        setSelectedReports(selectedReports.filter(id => id !== reportId))
    }

    const handleOpenDetails = (report: StudentDailyReport) => {
        setApprovalModal({ report, isOpen: true })
        setApprovedHours({
            physics: report.subjectBreakdown.physics,
            chemistry: report.subjectBreakdown.chemistry,
            biology: report.subjectBreakdown.biology
        })
        setMentorFeedback('')
    }

    const handleApproveDetailed = () => {
        const totalApproved = Object.values(approvedHours).reduce((sum, h) => sum + h, 0)
        toast.success(`Approved ${totalApproved}h with feedback`)
        setApprovalModal({ ...approvalModal, isOpen: false })
    }

    const handleBulkApprove = () => {
        toast.success(`Approved ${selectedReports.length} reports`)
        setSelectedReports([])
    }

    const getValidationColor = (reported: number, expected?: number) => {
        if (!expected) return 'text-green-600'
        const diff = Math.abs(reported - expected) / expected
        if (diff > 0.3) return 'text-red-600' // More than 30% off
        if (diff > 0.15) return 'text-yellow-600' // More than 15% off
        return 'text-green-600'
    }

    const getCompletionRate = (report: StudentDailyReport) => {
        // Simplified - in real app, compare with assigned tasks
        return report.tasksCompleted.length >= 3 ? 100 : (report.tasksCompleted.length / 3) * 100
    }

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            {/* Header */}
            <div>
                <h2 className="text-xl md:text-2xl font-bold text-theme-primary">Pending Approvals</h2>
                <p className="text-sm text-theme-secondary mt-1">
                    {pendingReports.length} report{pendingReports.length !== 1 ? 's' : ''} waiting for review
                </p>
            </div>

            {/* Bulk Actions Bar */}
            {selectedReports.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <GlassmorphicCard className="p-4 bg-blue-50 dark:bg-blue-900/10 border-l-4 border-l-primary-blue">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <p className="text-sm font-semibold text-theme-primary">
                                {selectedReports.length} report{selectedReports.length > 1 ? 's' : ''} selected
                            </p>
                            <div className="flex gap-2 w-full sm:w-auto">
                                <button
                                    onClick={handleBulkApprove}
                                    className="flex-1 sm:flex-none px-4 py-2 bg-green-500 text-white text-sm font-semibold rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                                >
                                    <CheckCircle2 className="h-4 w-4" />
                                    <span className="whitespace-nowrap">Approve All</span>
                                </button>
                                <button className="flex-1 sm:flex-none px-4 py-2 bg-amber-500 text-white text-sm font-semibold rounded-lg hover:bg-amber-600 transition-colors whitespace-nowrap">
                                    Apply -10%
                                </button>
                            </div>
                        </div>
                    </GlassmorphicCard>
                </motion.div>
            )}

            {/* Reports Table */}
            <GlassmorphicCard className="overflow-hidden">
                <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                        <input
                            type="checkbox"
                            checked={selectedReports.length === pendingReports.length && pendingReports.length > 0}
                            onChange={handleSelectAll}
                            className="rounded"
                        />
                        <span className="text-sm font-semibold text-theme-secondary">
                            Today - {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ({pendingReports.length} students)
                        </span>
                    </div>
                </div>

                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {pendingReports.map((report) => {
                        const completionRate = getCompletionRate(report)
                        const status = completionRate === 100 && report.reportedHours >= 6
                            ? '🟢 On track'
                            : completionRate < 70 || report.reportedHours < 4
                                ? '🔴 Below target'
                                : '🟡 Needs review'

                        return (
                            <div
                                key={report.id}
                                className="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
                            >
                                <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
                                    {/* Mobile Top Row: Checkbox + Name + Status Icon */}
                                    <div className="flex items-center gap-3 w-full md:w-auto md:flex-1 min-w-0">
                                        <input
                                            type="checkbox"
                                            checked={selectedReports.includes(report.id)}
                                            onChange={() => handleSelectReport(report.id)}
                                            className="rounded shrink-0"
                                        />

                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-blue to-blue-600 flex items-center justify-center text-white font-bold text-sm shrink-0">
                                                {report.studentName.split(' ').map(n => n[0]).join('')}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-semibold text-theme-primary truncate">{report.studentName}</p>
                                                <p className="text-xs text-theme-secondary flex items-center gap-2">
                                                    <span>Tasks: {report.tasksCompleted.length}/3 ✓</span>
                                                    <span className="md:hidden">• {status.split(' ')[0]}</span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Desktop Status */}
                                    <div className="hidden md:block w-32 shrink-0">
                                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-theme-secondary">
                                            {status}
                                        </span>
                                    </div>

                                    {/* Mobile Bottom Row / Desktop Right: Hours + Actions */}
                                    <div className="flex items-center justify-between w-full md:w-auto gap-4 pl-9 md:pl-0">
                                        <div className="flex items-center gap-2 shrink-0">
                                            <span className="text-sm font-medium text-theme-secondary">
                                                {report.reportedHours}h
                                            </span>
                                            <span className="text-theme-secondary/50">→</span>
                                            <input
                                                type="number"
                                                step="0.5"
                                                defaultValue={report.reportedHours}
                                                className="w-16 px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-theme-primary outline-none focus:border-primary-blue font-semibold text-center"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>

                                        <div className="flex gap-2 shrink-0">
                                            <button
                                                onClick={() => handleQuickApprove(report.id, report.reportedHours)}
                                                className="p-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                                                title="Quick Approve"
                                            >
                                                <CheckCircle2 className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={() => handleOpenDetails(report)}
                                                className="px-3 py-2 bg-primary-blue text-white text-xs font-semibold rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-1"
                                            >
                                                Details
                                                <ChevronRight className="h-3 w-3" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>

                {pendingReports.length === 0 && (
                    <div className="p-12 text-center">
                        <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                        <h3 className="text-lg font-bold text-theme-primary mb-2">All caught up!</h3>
                        <p className="text-theme-secondary">No pending approvals at the moment.</p>
                    </div>
                )}
            </GlassmorphicCard>

            {/* Detailed Review Modal */}
            <AnimatePresence>
                {approvalModal.isOpen && approvalModal.report && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setApprovalModal({ ...approvalModal, isOpen: false })}
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4"
                        >
                            <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                                <GlassmorphicCard className="p-6">
                                    <div className="space-y-4">
                                        {/* Header */}
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h3 className="text-xl font-bold text-theme-primary">
                                                    {approvalModal.report.studentName}
                                                </h3>
                                                <p className="text-sm text-theme-secondary">{approvalModal.report.date}</p>
                                            </div>
                                            <button
                                                onClick={() => setApprovalModal({ ...approvalModal, isOpen: false })}
                                                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                            >
                                                <XCircle className="h-5 w-5 text-theme-secondary" />
                                            </button>
                                        </div>

                                        {/* Hours Summary */}
                                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                            <p className="text-sm text-theme-secondary mb-2">Total Hours</p>
                                            <div className="flex items-center gap-3">
                                                <span className="text-2xl font-bold text-theme-primary">
                                                    {approvalModal.report.reportedHours}h
                                                </span>
                                                <span className="text-theme-secondary">→</span>
                                                <input
                                                    type="number"
                                                    step="0.5"
                                                    value={Object.values(approvedHours).reduce((sum, h) => sum + h, 0)}
                                                    className="text-2xl font-bold px-3 py-1 rounded-lg bg-white dark:bg-slate-900 border-2 border-primary-blue text-primary-blue outline-none w-24"
                                                    disabled
                                                />
                                            </div>
                                        </div>

                                        {/* Subject Breakdown */}
                                        <div>
                                            <p className="text-sm font-semibold text-theme-primary mb-2">Subject Breakdown</p>
                                            <div className="space-y-2">
                                                {(['physics', 'chemistry', 'biology'] as const).map((subject) => (
                                                    <div key={subject} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                                        <span className="text-sm font-medium text-theme-primary capitalize flex-1">
                                                            {subject}:
                                                        </span>
                                                        <span className="text-sm text-theme-secondary">
                                                            {approvalModal.report.subjectBreakdown[subject]}h
                                                        </span>
                                                        <input
                                                            type="number"
                                                            step="0.5"
                                                            value={approvedHours[subject] || 0}
                                                            onChange={(e) => setApprovedHours({
                                                                ...approvedHours,
                                                                [subject]: parseFloat(e.target.value) || 0
                                                            })}
                                                            className="w-20 px-2 py-1 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm text-theme-primary outline-none focus:border-primary-blue"
                                                        />
                                                        <span className={`text-xs font-semibold ${getValidationColor(approvalModal.report.subjectBreakdown[subject])}`}>
                                                            ✓ Looks right
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                            <button
                                                onClick={() => {
                                                    const adjusted = {
                                                        physics: Math.max(0, approvedHours.physics * 0.9),
                                                        chemistry: Math.max(0, approvedHours.chemistry * 0.9),
                                                        biology: Math.max(0, approvedHours.biology * 0.9)
                                                    }
                                                    setApprovedHours(adjusted)
                                                    toast.info('Applied -10% adjustment to all subjects')
                                                }}
                                                className="mt-2 px-4 py-2 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-sm font-semibold rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900/30 transition-colors"
                                            >
                                                📉 Apply -10% Adjustment
                                            </button>
                                        </div>

                                        {/* Tasks Completed */}
                                        <div>
                                            <p className="text-sm font-semibold text-theme-primary mb-2">
                                                Tasks Completed ({approvalModal.report.tasksCompleted.length})
                                            </p>
                                            <div className="space-y-2">
                                                {approvalModal.report.tasksCompleted.map((task) => (
                                                    <div key={task.id} className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/10 rounded-lg">
                                                        <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                                                        <span className="text-sm text-theme-primary flex-1">{task.title}</span>
                                                        <span className="text-xs text-theme-secondary">({task.duration}h)</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Student's Summary */}
                                        <div>
                                            <p className="text-sm font-semibold text-theme-primary mb-2">Student's Summary</p>
                                            <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                                <p className="text-sm text-theme-secondary italic">
                                                    "{approvalModal.report.workDescription}"
                                                </p>
                                            </div>
                                        </div>

                                        {/* Mentor Feedback */}
                                        <div>
                                            <p className="text-sm font-semibold text-theme-primary mb-2">Mentor Feedback (shown to student)</p>
                                            <textarea
                                                value={mentorFeedback}
                                                onChange={(e) => setMentorFeedback(e.target.value)}
                                                placeholder="Add your feedback here..."
                                                rows={3}
                                                className="w-full px-4 py-3 rounded-lg bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-700 focus:border-primary-blue text-theme-primary placeholder:text-theme-secondary outline-none resize-none"
                                            />
                                            <div className="flex gap-2 mt-2">
                                                <button className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-theme-secondary text-xs rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                                                    👍 Good job!
                                                </button>
                                                <button className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-theme-secondary text-xs rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                                                    ⭐ Excellent work!
                                                </button>
                                                <button className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-theme-secondary text-xs rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                                                    💪 Keep it up!
                                                </button>
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex gap-3 pt-4">
                                            <button className="flex-1 px-4 py-3 bg-slate-100 dark:bg-slate-800 text-theme-primary font-semibold rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                                                ❌ Reject & Comment
                                            </button>
                                            <button
                                                onClick={handleApproveDetailed}
                                                className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-green-500/25"
                                            >
                                                ✓ Approve with Feedback
                                            </button>
                                        </div>
                                    </div>
                                </GlassmorphicCard>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    )
}
